import {
  CheckCircleIcon,
  PencilSquareIcon,
  PhotoIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { observer } from "mobx-react-lite";
import { useMemo, useRef, useState } from "react";
import { Button, ButtonIntent } from "src/components/Button";
import { Cluster } from "src/components/Cluster";
import { StencilBarSlider } from "src/components/StencilBarSlider";
import type { MapState } from "src/types/MapState";
import type { World } from "src/types/World";
import { useTranslation } from "src/translations";
import { WMS_CATALOG } from "src/constants/wmsCatalog";
import {
  pointInBounds,
  type CuratedWmsEntry,
  type SavedWmsEntry,
} from "src/types/stencil/wmsCatalog";
import { ListToolButton } from "./ListToolButton";
import { Select } from "./Select";

interface Props {
  mapState: MapState;
  world: World | null;
}

export const StencilButton = observer(function StencilButton({ mapState, world }: Props) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<"file" | "url">("url");
  const [urlInput, setUrlInput] = useState("");
  const { stencil } = mapState;

  const wmsLayerItems = useMemo(() => {
    if (stencil.mode.kind !== "wms") return [];
    return stencil.mode.capabilities.layers.map((l) => ({ id: l.name, label: l.title }));
  }, [stencil.mode]);
  const selectedLayerName = stencil.mode.kind === "wms" ? stencil.mode.selectedLayerName : "";

  const point = mapState.viewportCenter;
  const [catalogQuery, setCatalogQuery] = useState("");
  const sortedCatalog = useMemo(() => {
    const q = catalogQuery.trim().toLowerCase();
    const matches = q
      ? WMS_CATALOG.filter(
          (e) =>
            e.title.toLowerCase().includes(q) || e.state.toLowerCase().includes(q)
        )
      : WMS_CATALOG;
    return [...matches].sort((a, b) => {
      const aIn = point ? pointInBounds(point, a.bounds) : false;
      const bIn = point ? pointInBounds(point, b.bounds) : false;
      if (aIn !== bIn) return aIn ? -1 : 1;
      if (a.state !== b.state) return a.state.localeCompare(b.state);
      return a.title.localeCompare(b.title);
    });
  }, [point, catalogQuery]);
  const savedEntries = world?.stencilCatalog ?? [];

  const onClickCurated = (entry: CuratedWmsEntry) => {
    void stencil.loadCatalogEntry(entry);
  };
  const onClickSaved = (entry: SavedWmsEntry) => {
    void stencil.loadCatalogEntry(entry);
  };
  const onLoadUrl = () => {
    if (!world) return;
    void stencil.setUrl(urlInput, world).then(() => {
      if (!stencil.error) setUrlInput("");
    });
  };

  return (
    <ListToolButton Icon={PhotoIcon} title={t("stencil.title")}>
      <div className="space-y-4 pt-2 min-w-[260px]">
        <div className="flex flex-1 justify-center items-center space-x-2 text-gray-200">
          <PhotoIcon className="w-4 h-4" />
          <span className="font-semibold">{t("stencil.title")}</span>
        </div>

        {/* ---- Pick a layer from the catalog ---- */}
        <div className="space-y-2">
          <div className="space-y-1">
            <span className="text-xs font-medium text-gray-100">
              {t("stencil.catalogDefaults")}
            </span>
            <input
              type="search"
              placeholder={t("stencil.searchPlaceholder")}
              value={catalogQuery}
              onChange={(e) => setCatalogQuery(e.target.value)}
              className="w-full text-xs text-gray-100 placeholder:text-gray-200 px-2 py-1 border border-gray-300 rounded"
            />
            {sortedCatalog.length > 0 ? (
              <ul className="max-h-[220px] overflow-y-auto space-y-1 pr-1">
                {sortedCatalog.map((entry) => {
                  const inBounds = point ? pointInBounds(point, entry.bounds) : false;
                  const isActive = stencil.loadedCatalogId === entry.id;
                  return (
                    <li key={entry.id}>
                      <button
                        aria-pressed={isActive}
                        className={
                          "w-full text-left text-xs px-2 py-1 rounded text-gray-100 " +
                          (isActive ? "bg-elevation-2" : "hover:bg-elevation-1")
                        }
                        onClick={() => onClickCurated(entry)}
                      >
                        <div className="flex items-center gap-1.5">
                          {inBounds && (
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-green-100 shrink-0"
                              aria-hidden
                            />
                          )}
                          <span className="flex-1">{entry.title}</span>
                          {isActive && (
                            <CheckCircleIcon className="w-3.5 h-3.5 text-green-100 shrink-0" />
                          )}
                        </div>
                        <div className="text-2xs text-gray-200">
                          {t(`stencil.cat.${entry.category}`)}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-xs text-gray-200 px-2 py-1">{t("stencil.noResults")}</p>
            )}
          </div>

          <Cluster visible={savedEntries.length > 0}>
            <div className="space-y-1">
              <span className="text-xs font-medium text-gray-100">
                {t("stencil.catalogSaved")}
              </span>
              <ul className="space-y-1">
                {savedEntries.map((entry) => (
                  <li key={entry.url} className="flex items-center gap-1">
                    <button
                      className="flex-1 text-left text-xs text-gray-100 px-2 py-1 rounded hover:bg-elevation-1"
                      onClick={() => onClickSaved(entry)}
                    >
                      {entry.title}
                    </button>
                    <button
                      className="p-1 rounded hover:bg-elevation-1 text-gray-200 hover:text-gray-100"
                      title={t("stencil.removeSaved")}
                      onClick={() => world?.removeFromStencilCatalog(entry.url)}
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </Cluster>
        </div>

        <Cluster visible={!!stencil.error}>
          <p className="text-red-400 text-xs">{stencil.error ? t(stencil.error) : ""}</p>
        </Cluster>

        {/* ---- Controls for the currently active overlay ---- */}
        <Cluster visible={stencil.hasContent}>
          <div className="space-y-3 border-t border-gray-300 pt-3">
            <span className="text-xs font-medium text-gray-100">
              {t("stencil.activeOverlay")}
            </span>

            <Cluster visible={stencil.isWms && wmsLayerItems.length > 1}>
              <div className="space-y-1">
                <span className="text-2xs text-gray-200">{t("stencil.wmsLayer")}</span>
                <Select
                  items={wmsLayerItems}
                  selected={selectedLayerName}
                  onChange={stencil.selectWmsLayer}
                />
              </div>
            </Cluster>

            <Cluster visible={stencil.isLocked && !stencil.editMode}>
              <Button
                label={t("stencil.editMode")}
                Icon={PencilSquareIcon}
                onClick={stencil.enterEditMode}
              />
            </Cluster>

            <Cluster visible={stencil.isWms}>
              <StencilBarSlider
                label={t("stencil.opacity")}
                min={0}
                max={100}
                value={stencil.opacity.value}
                onInput={stencil.opacity.set}
                onChange={stencil.opacity.set}
              />
            </Cluster>

            <Button
              label={t("stencil.remove")}
              intent={ButtonIntent.DANGER}
              onClick={stencil.clear}
            />
          </div>
        </Cluster>

        {/* ---- Add a custom layer (separate functionality) ---- */}
        <div className="space-y-2 border-t border-gray-300 pt-3">
          <span className="text-xs font-medium text-gray-100">
            {t("stencil.addCustom")}
          </span>
          <div className="flex rounded border border-gray-300 overflow-hidden text-xs">
            <button
              className={
                tab === "file"
                  ? "flex-1 bg-purple-100 text-white px-2 py-1"
                  : "flex-1 px-2 py-1 hover:bg-elevation-1"
              }
              onClick={() => setTab("file")}
            >
              {t("stencil.tabFile")}
            </button>
            <button
              className={
                tab === "url"
                  ? "flex-1 bg-purple-100 text-white px-2 py-1"
                  : "flex-1 px-2 py-1 hover:bg-elevation-1"
              }
              onClick={() => setTab("url")}
            >
              {t("stencil.tabUrl")}
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,.xml,.wms,application/xml,text/xml"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void stencil.setFile(file, world);
              e.target.value = "";
            }}
          />
          <Cluster visible={tab === "file"}>
            <Button
              label={t("stencil.uploadFile")}
              Icon={PhotoIcon}
              onClick={() => fileInputRef.current?.click()}
            />
          </Cluster>
          <Cluster visible={tab === "url"}>
            <div className="space-y-1">
              <input
                type="url"
                placeholder={t("stencil.urlPlaceholder")}
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onLoadUrl();
                }}
                className="w-full text-xs px-2 py-1 border border-gray-300 rounded"
              />
              <Button label={t("stencil.urlLoad")} onClick={onLoadUrl} />
            </div>
          </Cluster>
        </div>
      </div>
    </ListToolButton>
  );
});
